import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import { Button } from '../client/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../client/components/ui/card';

// Set up PDF.js worker using the matching worker from the same package version
// @ts-ignore - Vite will resolve this to a URL string
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

// Debug flag - set to false to disable debugging
const DEBUG_MEMORY = false;

// Hook to track PDF-specific debugging
const usePDFDebug = () => {
    useEffect(() => {
        if (!DEBUG_MEMORY) return;
        
        const logPDFState = () => {
            const canvases = document.querySelectorAll('canvas');
            const pdfContainers = document.querySelectorAll('[class*="pdf"]');
            
            console.log('[PDF-DEBUG] Canvases:', canvases.length);
            console.log('[PDF-DEBUG] PDF containers:', pdfContainers.length);
            
            // Check for stuck render tasks
            canvases.forEach((canvas, index) => {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    console.log(`[PDF-DEBUG] Canvas ${index}: ${canvas.width}x${canvas.height}`);
                }
            });
        };
        
        const interval = setInterval(logPDFState, 60000); // Every minute
        return () => clearInterval(interval);
    }, []);
};

// Hook to track component re-renders
const useRenderTracker = (componentName: string, props: any) => {
    const renderCount = useRef(0);
    const prevProps = useRef(props);
    
    renderCount.current++;
    
    if (DEBUG_MEMORY && renderCount.current % 10 === 0) {
        console.log(`[RENDER-COUNT] ${componentName}: ${renderCount.current} renders`);
        
        // Check for prop changes
        const changedProps = Object.keys(props).filter(
            key => props[key] !== prevProps.current[key]
        );
        
        if (changedProps.length > 0) {
            console.log(`[RENDER-PROPS] ${componentName} changed:`, changedProps);
        }
    }
    
    prevProps.current = props;
};

// Hook to track component lifecycle
const useComponentLifecycle = (componentName: string) => {
    useEffect(() => {
        if (DEBUG_MEMORY) console.log(`[LIFECYCLE] ${componentName} mounted`);
        
        return () => {
            if (DEBUG_MEMORY) console.log(`[LIFECYCLE] ${componentName} unmounting`);
        };
    }, [componentName]);
    
    useEffect(() => {
        return () => {
            if (DEBUG_MEMORY) console.log(`[LIFECYCLE] ${componentName} cleanup on dependency change`);
        };
    });
};

interface AdvancedPDFViewerProps {
    projectId: string;
    filename: string;
    highlightedTerm?: string;
    currentPage?: number;
    title?: string;
    onClearHighlight?: () => void;
    termUpdates?: Record<string, string>;
    isMinimized?: boolean;
    onToggleMinimize?: () => void;
}

interface SearchMatch {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface TextMatch {
    start: number;
    end: number;
    strategy: string;
}

export const AdvancedPDFViewer: React.FC<AdvancedPDFViewerProps> = ({
    projectId,
    filename,
    highlightedTerm = '',
    currentPage = 1,
    title = "Document Display",
    onClearHighlight,
    termUpdates = {},
    isMinimized = false,
    onToggleMinimize
}) => {
    console.log(`[AdvancedPDFViewer] Render - projectId: "${projectId}", filename: "${filename}", isMinimized: ${isMinimized}`);
    
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
    const currentCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const isRenderingRef = useRef<boolean>(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastSearchRef = useRef<{ term: string; page: number } | null>(null);
    const lastExternalPageRef = useRef<number | null>(null);
    
    // PDF Loading Debouncing State
    const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isLoadingPDFRef = useRef<boolean>(false);
    const pendingLoadRef = useRef<{ projectId: string; filename: string } | null>(null);
    const currentLoadKeyRef = useRef<string>('');
    const loadedPDFKeyRef = useRef<string>(''); // Track successfully loaded PDF
    
    // Component initialization tracking
    const isInitializingRef = useRef<boolean>(true);
    const componentMountTimeRef = useRef<number>(Date.now());
    
    const [pdfDocument, setPdfDocument] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
    const [pageNum, setPageNum] = useState(currentPage);
    const [numPages, setNumPages] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchMatches, setSearchMatches] = useState<SearchMatch[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [baseScale] = useState(1.5); // Determines the base size of the document at 100% zoom
    const [debugInfo, setDebugInfo] = useState<string>('');
    const [renderError, setRenderError] = useState<string | null>(null);
    const [pageInputValue, setPageInputValue] = useState('');
    const [canvasWidth, setCanvasWidth] = useState(0);
    const [canvasHeight, setCanvasHeight] = useState(0);
    const [actualCanvasWidth, setActualCanvasWidth] = useState(0);
    const [actualCanvasHeight, setActualCanvasHeight] = useState(0);
    const [hasManuallyNavigated, setHasManuallyNavigated] = useState(false);
    const manualNavigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastManualNavigationTimeRef = useRef<number | null>(null);
    const lastResetAttemptTimeRef = useRef<number | null>(null);

    // Add render tracking refs for debugging
    const lastRenderAttemptRef = useRef<string | null>(null);
    const lastRenderSuccessRef = useRef<string | null>(null);

    // Memoized pixel ratio for performance
    const pixelRatio = useMemo(() => window.devicePixelRatio || 1, []);

    // DEBUG: Add debugging hooks for AdvancedPDFViewer
    useRenderTracker('AdvancedPDFViewer', { projectId, filename, isMinimized, highlightedTerm });
    usePDFDebug();
    useComponentLifecycle('AdvancedPDFViewer');

    // Cleanup function for render tasks and timeouts - using ref to prevent recreation
    const cleanupRef = useRef<() => void>();
    cleanupRef.current = () => {
        if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
            renderTaskRef.current = null;
        }
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = null;
        }
        // Clear PDF loading debouncing timeout
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
        }
        // Clear manual navigation timeout
        if (manualNavigationTimeoutRef.current) {
            clearTimeout(manualNavigationTimeoutRef.current);
            manualNavigationTimeoutRef.current = null;
        }
        // Reset rendering state to prevent stuck loading
        isRenderingRef.current = false;
        // Clear any render errors
        setRenderError(null);
    };
    
    // Stable cleanup function that doesn't change on every render
    const cleanup = useCallback(() => {
        cleanupRef.current?.();
    }, []);

    // Enhanced cleanup on unmount
    useEffect(() => {
        return () => {
            cleanup();
            // Clear search matches to prevent memory leaks
            setSearchMatches([]);
        };
    }, [cleanup]);

    // Debounced PDF loading function to prevent rapid load/destroy cycles - using ref for stability
    const debouncedLoadPDFRef = useRef<(targetProjectId: string, targetFilename: string) => Promise<void>>();
    debouncedLoadPDFRef.current = async (targetProjectId: string, targetFilename: string) => {
        const loadKey = `${targetProjectId}:${targetFilename}`;
        
        // If we're already loading this exact PDF, don't start another load
        if (isLoadingPDFRef.current && currentLoadKeyRef.current === loadKey) {
            console.log(`[AdvancedPDFViewer] Already loading ${loadKey}, skipping duplicate request`);
            return;
        }
        
        // If there's a different load in progress, wait for it to complete
        if (isLoadingPDFRef.current) {
            console.log(`[AdvancedPDFViewer] Waiting for current load to complete before loading ${loadKey}`);
            let waitCount = 0;
            while (isLoadingPDFRef.current && waitCount < 50) { // Max 5 seconds wait
                await new Promise(resolve => setTimeout(resolve, 100));
                waitCount++;
            }
            
            // If still loading after timeout, force reset
            if (isLoadingPDFRef.current) {
                console.log(`[AdvancedPDFViewer] Force resetting stuck loading state`);
                isLoadingPDFRef.current = false;
            }
        }
        
        // Mark as loading this specific PDF
        isLoadingPDFRef.current = true;
        currentLoadKeyRef.current = loadKey;
        loadedPDFKeyRef.current = ''; // Clear previous loaded PDF
        
        try {
            setIsLoading(true);
            setError(null);
            setRenderError(null);
            setDebugInfo('');
            
            const { getApiUrl } = await import('../client/lib/config');
            // URL-encode filename to safely handle spaces, quotes, angle brackets, etc.
            const encodedFilename = encodeURIComponent(targetFilename);
            const pdfUrl = getApiUrl(`/uploads/${targetProjectId}/${encodedFilename}`);
            console.log(`[AdvancedPDFViewer] Loading PDF from: ${pdfUrl}`);
            console.log(`[AdvancedPDFViewer] ProjectId: "${targetProjectId}", Filename: "${targetFilename}"`);
            
            // Test if the URL is accessible first
            try {
                const response = await fetch(pdfUrl, { method: 'HEAD' });
                console.log(`[AdvancedPDFViewer] URL accessibility test - Status: ${response.status}`);
                if (!response.ok) {
                    throw new Error(`PDF file not accessible: ${response.status} ${response.statusText}`);
                }
            } catch (fetchError: any) {
                console.error(`[AdvancedPDFViewer] URL accessibility test failed:`, fetchError);
                // Do not throw for transient 404 during stage transitions; retry quickly once
                if (currentLoadKeyRef.current === loadKey) {
                    await new Promise(r => setTimeout(r, 200));
                }
                throw new Error(`Cannot access PDF file: ${fetchError.message}`);
            }
            
            // Ensure cleanup is complete before loading new PDF
            cleanup();
            
            // Small delay to ensure cleanup operations complete
            await new Promise(resolve => setTimeout(resolve, 50));
            
            const loadingTask = pdfjsLib.getDocument({
                url: pdfUrl,
                verbosity: 0,
                // Add timeout and retry configuration
                httpHeaders: {
                    'Accept': 'application/pdf',
                    'Cache-Control': 'no-cache'
                },
                // Increase timeout for large PDFs
                timeout: 30000, // 30 seconds
                // Enable range requests for better streaming
                disableRange: false,
                disableStream: false
            });
            
            const pdf = await loadingTask.promise;
            console.log(`[AdvancedPDFViewer] PDF loaded successfully. Pages: ${pdf.numPages}`);
            
            // Only update state if we're still loading the same PDF (prevent race conditions)
            if (currentLoadKeyRef.current === loadKey) {
                setPdfDocument(pdf);
                setNumPages(pdf.numPages);
                setDebugInfo('');
                setIsLoading(false);
                loadedPDFKeyRef.current = loadKey; // Mark as successfully loaded
                
                // Reset initialization state for new PDF
                isInitializingRef.current = true;
                componentMountTimeRef.current = Date.now();
            } else {
                console.log(`[AdvancedPDFViewer] Load completed but target changed, discarding result for ${loadKey}`);
                // Clean up the loaded PDF since we don't need it
                try { pdf.destroy(); } catch {}
                // Ensure we clear loading state so the panel doesn't remain blank
                if (isLoadingPDFRef.current) {
                    isLoadingPDFRef.current = false;
                }
            }
            
        } catch (err: any) {
            console.error('Error loading PDF:', err);
            // Only update error state if we're still loading the same PDF
            if (currentLoadKeyRef.current === loadKey) {
                setError(`Failed to load PDF: ${err.message}`);
                setDebugInfo(`Error: ${err.message}`);
                setIsLoading(false);
            }
        } finally {
            // Only reset loading state if we're still the current load
            if (currentLoadKeyRef.current === loadKey) {
                isLoadingPDFRef.current = false;
                currentLoadKeyRef.current = '';
            }
        }
    };
    
    // Stable wrapper function
    const debouncedLoadPDF = useCallback(async (targetProjectId: string, targetFilename: string) => {
        return debouncedLoadPDFRef.current?.(targetProjectId, targetFilename);
    }, []);

    // Load PDF document with stable dependencies to prevent constant re-renders
    useEffect(() => {
        if (!projectId || !filename) {
            console.log(`[AdvancedPDFViewer] Missing projectId or filename - projectId: "${projectId}", filename: "${filename}"`);
            setError('Missing project ID or filename');
            return;
        }
        
        const loadKey = `${projectId}:${filename}`;
        
        // If this PDF is already successfully loaded, skip
        if (loadedPDFKeyRef.current === loadKey && pdfDocument) {
            console.log(`[AdvancedPDFViewer] PDF ${loadKey} already successfully loaded, skipping`);
            return;
        }
        
        // If this is the same PDF we're currently loading, skip
        if (currentLoadKeyRef.current === loadKey) {
            console.log(`[AdvancedPDFViewer] PDF ${loadKey} currently loading, skipping`);
            return;
        }
        
        console.log(`[AdvancedPDFViewer] Scheduling PDF load for: ${loadKey}`);
        
        // Clear any existing timeout
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
        }
        
        // Store the pending load request
        pendingLoadRef.current = { projectId, filename };
        
        // Reset initialization state for new PDF loading
        isInitializingRef.current = true;
        componentMountTimeRef.current = Date.now();
        
        // Debounce the load with a short delay to prevent rapid firing
        loadTimeoutRef.current = setTimeout(() => {
            const pending = pendingLoadRef.current;
            // If props changed during debounce, abandon and schedule the new one next effect
            if (!(pending && pending.projectId === projectId && pending.filename === filename)) {
                console.log(`[AdvancedPDFViewer] Pending load cancelled or changed for: ${loadKey}`);
                loadTimeoutRef.current = null;
                return;
            }
            console.log(`[AdvancedPDFViewer] Executing debounced PDF load for: ${loadKey}`);
            setHasManuallyNavigated(false); // Reset navigation flag for new PDF
            // Clear any manual navigation timeout when loading new PDF
            if (manualNavigationTimeoutRef.current) {
                clearTimeout(manualNavigationTimeoutRef.current);
                manualNavigationTimeoutRef.current = null;
            }
            debouncedLoadPDF(pending.projectId, pending.filename);
            loadTimeoutRef.current = null;
        }, 150); // 150ms debounce delay - short enough to feel responsive, long enough to prevent rapid cycles
        
    }, [projectId, filename]); // Removed problematic dependencies: debouncedLoadPDF, pdfDocument

    // Clear search highlighting when moving to a new stage (projectId change)
    const previousProjectIdRef = useRef<string | null>(null);
    useEffect(() => {
        // Skip the initial mount
        if (previousProjectIdRef.current === null) {
            previousProjectIdRef.current = projectId;
            return;
        }
        
        // Check if projectId has changed (indicates stage change)
        if (previousProjectIdRef.current !== projectId) {
            console.log(`[AdvancedPDFViewer] Stage change detected (${previousProjectIdRef.current} -> ${projectId}), clearing search highlighting`);
            
            // Clear search state
            setSearchMatches([]);
            setDebugInfo('');
            setCurrentMatchIndex(0);
            setIsSearching(false);
            
            // Clear search cache
            lastSearchRef.current = null;
            
            // Clear any pending search timeout
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
            
            // Update the previous projectId reference
            previousProjectIdRef.current = projectId;
        }
    }, [projectId]);

    // Navigate to specific page when currentPage prop changes - IMPROVED LOGIC
    useEffect(() => {
        // Only navigate if we have a valid document and the page is different
        if (!pdfDocument || numPages === 0) {
            return;
        }

        // Mark component as no longer initializing after we have a loaded document
        if (isInitializingRef.current && pdfDocument && numPages > 0) {
            const timeSinceMount = Date.now() - componentMountTimeRef.current;
            console.log(`[AdvancedPDFViewer] Component initialization complete after ${timeSinceMount}ms`);
            isInitializingRef.current = false;
        }

        // Log current state for debugging
        console.log(`[AdvancedPDFViewer] Navigation effect triggered - currentPage: ${currentPage}, pageNum: ${pageNum}, hasManuallyNavigated: ${hasManuallyNavigated}, numPages: ${numPages}, isInitializing: ${isInitializingRef.current}`);

        // ENHANCED PROTECTION: Never allow navigation to page 1 unless it's truly legitimate
        if (currentPage === 1 && pageNum !== 1) {
            const timeSinceManualNav = Date.now() - (lastManualNavigationTimeRef.current || 0);
            const isNewExternalNavigation = lastExternalPageRef.current !== currentPage;
            
            // Only allow page 1 navigation if:
            // 1. We're initializing AND the component just mounted (first 2 seconds)
            // 2. It's been more than 30 seconds since manual navigation (very long timeout)
            // 3. It's a new external navigation that seems legitimate (not a parent re-render)
            const timeSinceMount = Date.now() - componentMountTimeRef.current;
            const isLegitimateInitialization = isInitializingRef.current && timeSinceMount < 2000;
            const isVeryOldManualNavigation = timeSinceManualNav > 30000; // 30 seconds
            const isLegitimateExternalNav = isNewExternalNavigation && !hasManuallyNavigated;
            
            if (isLegitimateInitialization) {
                console.log(`[AdvancedPDFViewer] Allowing page 1 navigation during legitimate initialization (${timeSinceMount}ms since mount)`);
                setPageNum(currentPage);
                setPageInputValue(currentPage.toString());
                lastExternalPageRef.current = currentPage;
            } else if (isVeryOldManualNavigation && isLegitimateExternalNav) {
                console.log(`[AdvancedPDFViewer] Allowing page 1 navigation after very long timeout (${timeSinceManualNav}ms since manual nav)`);
                setPageNum(currentPage);
                setPageInputValue(currentPage.toString());
                setHasManuallyNavigated(false); // Reset only now
                lastExternalPageRef.current = currentPage;
            } else {
                console.warn(`[AdvancedPDFViewer] BLOCKED page 1 reset (pageNum was ${pageNum}) - reasons: legitimateInit=${isLegitimateInitialization}, veryOldManualNav=${isVeryOldManualNavigation}, legitimateExternal=${isLegitimateExternalNav}, timeSinceManualNav=${timeSinceManualNav}ms`);
                
                // Track frequent reset attempts
                const timeSinceLastReset = Date.now() - (lastResetAttemptTimeRef.current || 0);
                lastResetAttemptTimeRef.current = Date.now();
                
                if (timeSinceLastReset < 5000) {
                    console.warn(`[AdvancedPDFViewer] Frequent reset attempts detected (${timeSinceLastReset}ms ago). Parent component re-rendering issue.`);
                }
            }
            return; // Exit early for all page 1 cases
        }

        // Map external currentPage (pdf-indexed) is already expected to be PDF page.
        // Check if this is a valid external navigation request (non-page-1)
        if (currentPage && 
            currentPage !== pageNum && 
            currentPage >= 1 && 
            currentPage <= numPages &&
            currentPage !== 1) { // Already handled page 1 above
            
            console.log(`[AdvancedPDFViewer] External navigation request: ${pageNum} -> ${currentPage} (hasManuallyNavigated: ${hasManuallyNavigated})`);
            
            // If this is a new external navigation (different from the last one we received),
            // it means the user clicked a new term in the table, so we should navigate
            const isNewExternalNavigation = lastExternalPageRef.current !== currentPage;
            
            // ENHANCED PROTECTION: If user has manually navigated, only allow external navigation if:
            // 1. It's a meaningful new external navigation (not a reset)
            // 2. The target page is significantly different (suggests intentional navigation)  
            // 3. Component is not in initialization phase
            // 4. It's been at least 2 seconds since manual navigation (prevent rapid interference)
            const timeSinceManualNav = Date.now() - (lastManualNavigationTimeRef.current || 0);
            const shouldOverrideManualNavigation = !hasManuallyNavigated || (
                isNewExternalNavigation && 
                Math.abs(currentPage - pageNum) > 1 && // Only for non-adjacent pages (suggests intentional navigation)
                !isInitializingRef.current && // Don't override during component initialization
                timeSinceManualNav > 2000 // Wait at least 2 seconds after manual navigation
            );
            
            if (shouldOverrideManualNavigation) {
                console.log(`[AdvancedPDFViewer] Navigating to page ${currentPage} (new external: ${isNewExternalNavigation}, manual flag: ${hasManuallyNavigated}, override allowed: ${shouldOverrideManualNavigation})`);
                
                // Navigate to the requested page
                setPageNum(currentPage);
                setPageInputValue(currentPage.toString());
                
                // Only reset manual navigation flag for legitimate external navigation
                if (isNewExternalNavigation && !hasManuallyNavigated) {
                    setHasManuallyNavigated(false);
                }
                
                // Track this external page for future comparisons
                lastExternalPageRef.current = currentPage;
            } else {
                console.log(`[AdvancedPDFViewer] BLOCKED external navigation to ${currentPage} - protecting manual navigation (reasons: hasManuallyNavigated=${hasManuallyNavigated}, isNewExternal=${isNewExternalNavigation}, isNonAdjacent=${Math.abs(currentPage - pageNum) > 1}, isInitializing=${isInitializingRef.current}, timeSinceManualNav=${timeSinceManualNav}ms)`);
            }
        }
        
        // Handle the case where pageNum is not set but currentPage is valid (component initialization)
        // This prevents the component from staying on page 1 when it should initialize to a different page
        // BUT ONLY during actual initialization, not during subsequent renders
        else if (currentPage && 
                 pageNum === 1 && 
                 currentPage !== 1 && 
                 currentPage >= 1 && 
                 currentPage <= numPages && 
                 !hasManuallyNavigated &&
                 pdfDocument &&
                 isInitializingRef.current) { // MORE STRICT: Only during actual initialization
            console.log(`[AdvancedPDFViewer] Initial page sync: setting pageNum from ${pageNum} to ${currentPage}`);
            setPageNum(currentPage);
            setPageInputValue(currentPage.toString());
            lastExternalPageRef.current = currentPage;
        }
    }, [currentPage, numPages, pageNum, pdfDocument, hasManuallyNavigated]);

    // Update page input when pageNum changes
    useEffect(() => {
        setPageInputValue(pageNum.toString());
    }, [pageNum]);

    // Create a fresh canvas for rendering with optimization
    const createFreshCanvas = useCallback((): HTMLCanvasElement => {
        const canvas = document.createElement('canvas');
        canvas.className = 'block max-w-none h-auto origin-top-left';
        canvas.style.border = '1px solid #e5e7eb';
        canvas.style.cursor = 'grab';
        return canvas;
    }, []);

    // Optimized render function with proper cleanup
    const renderPage = useCallback(async (pageNumber: number) => {
        if (!pdfDocument || !canvasContainerRef.current) {
            console.log('[AdvancedPDFViewer] Cannot render: missing document or container');
            return;
        }

        // Safety check: if component is minimized, don't render
        if (isMinimized) {
            console.log('[AdvancedPDFViewer] Skipping render: component is minimized');
            return;
        }

        // Cancel any existing render task
        if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
            renderTaskRef.current = null;
        }

        // Clear any previous render errors when starting a new render
        setRenderError(null);

        // Prevent concurrent renders with timeout safety
        if (isRenderingRef.current) {
            console.log(`[AdvancedPDFViewer] Already rendering, waiting...`);
            // Add timeout to prevent infinite waiting
            let waitCount = 0;
            while (isRenderingRef.current && waitCount < 10) {
                await new Promise(resolve => setTimeout(resolve, 100));
                waitCount++;
            }
            // If still rendering after timeout, force reset
            if (isRenderingRef.current) {
                console.log(`[AdvancedPDFViewer] Force resetting stuck render state`);
                isRenderingRef.current = false;
            }
        }

        isRenderingRef.current = true;
        
        try {
            console.log(`[AdvancedPDFViewer] Starting optimized render for page ${pageNumber}`);
            lastRenderAttemptRef.current = `Page ${pageNumber} at ${new Date().toLocaleTimeString()}`;

            const newCanvas = createFreshCanvas();
            const page = await pdfDocument.getPage(pageNumber);
            const context = newCanvas.getContext('2d', { willReadFrequently: false });
            
            if (!context) {
                throw new Error('Could not get canvas 2D context');
            }

            // Render the canvas at a high resolution for quality, based on the baseScale
            const renderScale = baseScale * 2 * pixelRatio; // Render at 2x the base size for clarity
            const viewport = page.getViewport({ scale: renderScale });
            
            // Set canvas bitmap dimensions
            newCanvas.width = viewport.width;
            newCanvas.height = viewport.height;
            
            // The display size is determined by the unzoomed baseScale
            const displayViewport = page.getViewport({ scale: baseScale });
            const displayWidth = displayViewport.width;
            const displayHeight = displayViewport.height;
            
            // The canvas element will fill its container, which is sized by canvasWidth/Height
            newCanvas.style.width = `100%`;
            newCanvas.style.height = `100%`;
            
            // Store the base dimensions for the container div
            setCanvasWidth(displayWidth);
            setCanvasHeight(displayHeight);
            
            // Clear and render
            context.clearRect(0, 0, newCanvas.width, newCanvas.height);
            
            const renderContext = {
                canvasContext: context,
                viewport: viewport
            };
            
            const renderTask = page.render(renderContext);
            renderTaskRef.current = renderTask;
            
            await renderTask.promise;
            
            // Replace canvas in DOM
            const container = canvasContainerRef.current;
            if (container) {
                // MORE ROBUST: Clean up ALL old canvases/elements in the container
                while (container.firstChild) {
                    container.removeChild(container.firstChild);
                }
                
                // Add new canvas
                container.appendChild(newCanvas);
                currentCanvasRef.current = newCanvas;
            }
            
            console.log(`[AdvancedPDFViewer] Page ${pageNumber} rendered successfully`);
            lastRenderSuccessRef.current = `Page ${pageNumber} at ${new Date().toLocaleTimeString()}`;
            
        } catch (error: any) {
            console.error(`[AdvancedPDFViewer] Error rendering page ${pageNumber}:`, error);
            
            // Don't show error for cancelled renders (normal when toggling visibility)
            if (error.name === 'RenderingCancelledException' || error.message?.includes('cancelled')) {
                console.log(`[AdvancedPDFViewer] Render cancelled for page ${pageNumber} (normal during visibility toggle)`);
                return;
            }
            
            // Only set error for actual rendering problems
            setRenderError(`Failed to render page ${pageNumber}: ${error.message}`);
        } finally {
            isRenderingRef.current = false;
            renderTaskRef.current = null;
        }
    }, [pdfDocument, baseScale, pixelRatio, isMinimized]);

    // Robust search function - tries multiple strategies to catch all matches
    const searchInPage = useCallback(async (searchTerm: string): Promise<SearchMatch[]> => {
        if (!pdfDocument || !searchTerm) {
            console.log('[AdvancedPDFViewer] Cannot search: no PDF document or search term');
            return [];
        }

        // Validate page number before attempting to access it
        if (pageNum < 1 || pageNum > numPages) {
            console.warn(`[AdvancedPDFViewer] Invalid page number ${pageNum}. Valid range is 1-${numPages}. Skipping search.`);
            return [];
        }

        try {
            console.log(`[AdvancedPDFViewer] Starting robust search for "${searchTerm}" on page ${pageNum} (valid range: 1-${numPages})`);
            
            let page;
            try {
                page = await pdfDocument.getPage(pageNum);
            } catch (pageError) {
                console.error(`[AdvancedPDFViewer] Failed to get page ${pageNum}:`, pageError);
                throw new Error(`Invalid page request: page ${pageNum} not found in document (valid range: 1-${numPages})`);
            }
            
            const textContent = await page.getTextContent();
            const viewport = page.getViewport({ scale: baseScale });
            const allMatches: SearchMatch[] = [];
            
            // Prepare search term variants
            const originalTerm = searchTerm.trim();
            const normalizedTerm = originalTerm.toLowerCase();
            
            console.log(`[AdvancedPDFViewer] Searching for: "${originalTerm}"`);
            
            // Build comprehensive text mapping with FIXED position tracking
            const textItems: any[] = [];
            let reconstructedText = '';
            
            // First pass: build the complete reconstructed text with all spaces
            const rawTextItems: any[] = [];
            textContent.items.forEach((item: any, index: number) => {
                if (item.str && typeof item.str === 'string') {
                    const text = item.str;
                    rawTextItems.push({ ...item, text, originalIndex: index });
                    
                    reconstructedText += text;
                    
                    // Add space if needed (smart spacing)
                    if (index < textContent.items.length - 1) {
                        const nextItem = textContent.items[index + 1] as any;
                        if (nextItem && nextItem.str) {
                            if (!text.match(/\s$/) && !nextItem.str.match(/^\s/)) {
                                reconstructedText += ' ';
                            }
                        }
                    }
                }
            });
            
            // Second pass: calculate correct positions based on final reconstructedText
            let currentPos = 0;
            for (const rawItem of rawTextItems) {
                const text = rawItem.text;
                const startPos = currentPos;
                currentPos += text.length;
                const endPos = currentPos;
                
                textItems.push({
                    ...rawItem,
                    startPos: startPos,
                    endPos: endPos,
                    index: rawItem.originalIndex
                });
                
                // Account for spaces that were added
                const nextIndex = rawItem.originalIndex + 1;
                if (nextIndex < textContent.items.length) {
                    const nextItem = textContent.items[nextIndex] as any;
                    if (nextItem && nextItem.str) {
                        if (!text.match(/\s$/) && !nextItem.str.match(/^\s/)) {
                            currentPos += 1; // Account for the space we added
                        }
                    }
                }
            }
            
            console.log(`[AdvancedPDFViewer] Reconstructed text length: ${reconstructedText.length} characters`);
            console.log(`[AdvancedPDFViewer] Text preview: "${reconstructedText.substring(0, 200)}..."`);
            
            // Strategy 1: Direct substring search (most reliable)
            const findMatches = (searchText: string, targetText: string, strategyName: string): TextMatch[] => {
                const matches: TextMatch[] = [];
                const lowerTarget = targetText.toLowerCase();
                const lowerSearch = searchText.toLowerCase();
                
                let startIndex = 0;
                while (true) {
                    const foundIndex = lowerTarget.indexOf(lowerSearch, startIndex);
                    if (foundIndex === -1) break;
                    
                    matches.push({
                        start: foundIndex,
                        end: foundIndex + lowerSearch.length,
                        strategy: strategyName
                    });
                    
                    startIndex = foundIndex + 1;
                }
                
                return matches;
            };
            
            // Try multiple search strategies - IMPROVED: Less aggressive to reduce false positives
            const searchStrategies = [
                { text: reconstructedText, name: 'exact', term: originalTerm },
                { text: reconstructedText, name: 'normalized', term: normalizedTerm },
                { text: reconstructedText.replace(/\s+/g, ' '), name: 'spacenorm', term: normalizedTerm },
                { text: reconstructedText.replace(/[.,;:!?'"()\-]/g, ''), name: 'nopunct', term: normalizedTerm.replace(/[.,;:!?'"()\-]/g, '') }
                // REMOVED: nospace strategy (too aggressive - causes false matches)
                // REMOVED: overly broad partial matching strategies
            ];
            
            // Add CONSERVATIVE partial matching strategies for multi-word terms only
            if (normalizedTerm.includes(' ')) {
                const words = normalizedTerm.split(/\s+/);
                
                // Only try removing first/last word if the remaining portion is still meaningful (3+ chars)
                if (words.length === 2) {
                    // For 2-word terms, try each word individually as last resort
                    const firstWord = words[0];
                    const secondWord = words[1];
                    if (firstWord.length >= 3) {
                        searchStrategies.push({ text: reconstructedText, name: 'first-word-only', term: firstWord });
                    }
                    if (secondWord.length >= 3) {
                        searchStrategies.push({ text: reconstructedText, name: 'second-word-only', term: secondWord });
                    }
                } else if (words.length > 2) {
                    // For 3+ word terms, try removing first or last word if remainder is meaningful
                    const withoutFirst = words.slice(1).join(' ');
                    const withoutLast = words.slice(0, -1).join(' ');
                    
                    if (withoutFirst.length >= 5) {
                        searchStrategies.push({ text: reconstructedText, name: 'without-first', term: withoutFirst });
                    }
                    if (withoutLast.length >= 5) {
                        searchStrategies.push({ text: reconstructedText, name: 'without-last', term: withoutLast });
                    }
                }
            }
            
            const foundMatches: TextMatch[] = [];
            let bestMatchType = '';
            
            for (const strategy of searchStrategies) {
                if (strategy.term.length < 2) continue;
                
                const matches = findMatches(strategy.term, strategy.text, strategy.name);
                if (matches.length > 0) {
                    console.log(`[AdvancedPDFViewer] Strategy "${strategy.name}" found ${matches.length} matches for "${strategy.term}"`);
                    
                    // IMPROVED: More conservative strategy prioritization
                    if (strategy.name === 'exact') {
                        // Exact matches are best - clear everything and use only these
                        console.log(`[AdvancedPDFViewer] Found EXACT matches, using only these`);
                        foundMatches.length = 0;
                        foundMatches.push(...matches);
                        bestMatchType = strategy.name;
                        break;
                    } else if (strategy.name === 'normalized' && !bestMatchType) {
                        // Normalized matches are second best - use only if no exact matches
                        console.log(`[AdvancedPDFViewer] Found NORMALIZED matches, clearing previous results`);
                        foundMatches.length = 0;
                        foundMatches.push(...matches);
                        bestMatchType = strategy.name;
                    } else if ((strategy.name === 'spacenorm' || strategy.name === 'nopunct') && 
                               (!bestMatchType || bestMatchType.includes('word-only'))) {
                        // These are better than partial word matches
                        console.log(`[AdvancedPDFViewer] Found ${strategy.name} matches, replacing less reliable matches`);
                        foundMatches.length = 0;
                        foundMatches.push(...matches);
                        bestMatchType = strategy.name;
                    } else if (strategy.name.includes('without-') && !bestMatchType) {
                        // Partial phrase matches - only if nothing better found
                        foundMatches.push(...matches);
                        bestMatchType = strategy.name;
                    } else if (strategy.name.includes('word-only') && !bestMatchType) {
                        // Single word matches - last resort
                        foundMatches.push(...matches);
                        bestMatchType = strategy.name;
                    }
                    
                    // For exact and normalized matches, don't try other strategies
                    if (strategy.name === 'exact' || strategy.name === 'normalized') {
                        break;
                    }
                }
            }
            
            // Remove duplicate matches (same or very close positions)
            const uniqueMatches: TextMatch[] = [];
            for (const match of foundMatches) {
                const isDuplicate = uniqueMatches.some(existing => 
                    Math.abs(existing.start - match.start) <= 2
                );
                if (!isDuplicate) {
                    uniqueMatches.push(match);
                }
            }
            
            console.log(`[AdvancedPDFViewer] Found ${uniqueMatches.length} unique text matches using "${bestMatchType}" strategy`);
            
            // Convert text matches to visual highlights
            const preliminaryHighlights: SearchMatch[] = [];
            
            for (const match of uniqueMatches) {
                // VALIDATION: Verify the match actually contains words from the search term
                const matchedText = reconstructedText.substring(match.start, match.end);
                const searchWords = normalizedTerm.toLowerCase().split(/\s+/).filter(word => word.length > 1);
                const matchedWords = matchedText.toLowerCase().split(/\s+/).filter(word => word.length > 1);
                
                // Check if at least one word from the search term appears in the matched text
                const hasValidWord = searchWords.some(searchWord => 
                    matchedWords.some(matchedWord => 
                        matchedWord.includes(searchWord) || searchWord.includes(matchedWord)
                    )
                );
                
                if (!hasValidWord) {
                    console.log(`[AdvancedPDFViewer] REJECTED false match: "${matchedText}" does not contain any words from "${normalizedTerm}"`);
                    continue; // Skip this match as it's likely a false positive
                }
                
                console.log(`[AdvancedPDFViewer] VALIDATED match: "${matchedText}" contains words from "${normalizedTerm}"`);
                
                // Find all text items that overlap with this match
                const overlappingItems = textItems.filter(item => {
                    const itemStart = item.startPos;
                    const itemEnd = item.endPos;
                    
                    // Check for any overlap
                    return match.start < itemEnd && match.end > itemStart;
                });
                
                console.log(`[AdvancedPDFViewer] Match at ${match.start}-${match.end} ("${matchedText}") overlaps with ${overlappingItems.length} text items`);
                
                // Create highlights for overlapping items
                for (const item of overlappingItems) {
                    try {
                        const transform = item.transform;
                        if (!transform || transform.length < 6) {
                            continue;
                        }
                        
                        // Calculate visual position
                        const baseX = transform[4] * baseScale;
                        const baseY = transform[5] * baseScale;
                        const height = (item.height || 12) * baseScale;
                        const width = (item.width || (item.text.length * (item.height || 12) * 0.6)) * baseScale;
                        
                        // Convert PDF coordinates to canvas coordinates
                        const canvasX = baseX;
                        const canvasY = viewport.height - baseY - height;
                        
                        const highlightRect = {
                            x: Math.max(0, canvasX - 1),
                            y: Math.max(0, canvasY - 1),
                            width: width + 2,
                            height: height + 2
                        };
                        
                        preliminaryHighlights.push(highlightRect);
                        console.log(`[AdvancedPDFViewer] Added preliminary highlight for "${item.text}" at (${highlightRect.x.toFixed(1)}, ${highlightRect.y.toFixed(1)})`);
                        
                    } catch (err) {
                        console.error(`[AdvancedPDFViewer] Error creating highlight:`, err);
                    }
                }
            }
            
            // Advanced highlight consolidation to reduce false multiple matches
            const consolidatedHighlights: SearchMatch[] = [];
            const MERGE_THRESHOLD = 15; // Pixels - merge highlights within this distance
            const OVERLAP_THRESHOLD = 0.3; // 30% overlap threshold
            
            // Sort highlights by position for efficient processing
            preliminaryHighlights.sort((a, b) => {
                const aCenter = a.y + a.height / 2;
                const bCenter = b.y + b.height / 2;
                if (Math.abs(aCenter - bCenter) < 5) {
                    // Same line, sort by x
                    return a.x - b.x;
                }
                return aCenter - bCenter;
            });
            
            for (const highlight of preliminaryHighlights) {
                let merged = false;
                
                // Try to merge with existing consolidated highlights
                for (let i = 0; i < consolidatedHighlights.length; i++) {
                    const existing = consolidatedHighlights[i];
                    
                    // Check if highlights are close enough to merge
                    const horizontalDistance = Math.abs(highlight.x - existing.x);
                    const verticalDistance = Math.abs(highlight.y - existing.y);
                    const heightDifference = Math.abs(highlight.height - existing.height);
                    
                    // Calculate overlap area
                    const overlapX = Math.max(0, Math.min(highlight.x + highlight.width, existing.x + existing.width) - Math.max(highlight.x, existing.x));
                    const overlapY = Math.max(0, Math.min(highlight.y + highlight.height, existing.y + existing.height) - Math.max(highlight.y, existing.y));
                    const overlapArea = overlapX * overlapY;
                    const highlightArea = highlight.width * highlight.height;
                    const existingArea = existing.width * existing.height;
                    const overlapRatio = overlapArea / Math.min(highlightArea, existingArea);
                    
                    // Merge conditions:
                    // 1. Very close horizontally and vertically (adjacent text fragments)
                    // 2. Significant overlap (overlapping text elements)
                    // 3. Same line with reasonable horizontal distance
                    const isAdjacent = horizontalDistance <= MERGE_THRESHOLD && verticalDistance <= 5 && heightDifference <= 5;
                    const hasSignificantOverlap = overlapRatio >= OVERLAP_THRESHOLD;
                    const isSameLine = verticalDistance <= 5 && horizontalDistance <= highlight.width + existing.width;
                    
                    if (isAdjacent || hasSignificantOverlap || isSameLine) {
                        // Merge highlights by creating a bounding box
                        const minX = Math.min(highlight.x, existing.x);
                        const minY = Math.min(highlight.y, existing.y);
                        const maxX = Math.max(highlight.x + highlight.width, existing.x + existing.width);
                        const maxY = Math.max(highlight.y + highlight.height, existing.y + existing.height);
                        
                        consolidatedHighlights[i] = {
                            x: minX,
                            y: minY,
                            width: maxX - minX,
                            height: maxY - minY
                        };
                        
                        merged = true;
                        console.log(`[AdvancedPDFViewer] Merged highlight at (${highlight.x.toFixed(1)}, ${highlight.y.toFixed(1)}) with existing at (${existing.x.toFixed(1)}, ${existing.y.toFixed(1)})`);
                        break;
                    }
                }
                
                // If not merged, add as new highlight
                if (!merged) {
                    consolidatedHighlights.push(highlight);
                }
            }
            
            // Final deduplication pass - remove highlights that are still too similar
            const finalHighlights: SearchMatch[] = [];
            for (const highlight of consolidatedHighlights) {
                const isDuplicate = finalHighlights.some(existing => {
                    const centerDistance = Math.sqrt(
                        Math.pow((highlight.x + highlight.width/2) - (existing.x + existing.width/2), 2) +
                        Math.pow((highlight.y + highlight.height/2) - (existing.y + existing.height/2), 2)
                    );
                    return centerDistance < 10; // 10 pixel center distance threshold
                });
                
                if (!isDuplicate) {
                    finalHighlights.push(highlight);
                }
            }
            
            console.log(`[AdvancedPDFViewer] Highlight consolidation: ${preliminaryHighlights.length} preliminary → ${consolidatedHighlights.length} consolidated → ${finalHighlights.length} final highlights`);
            console.log(`[AdvancedPDFViewer] Robust search completed. Found ${finalHighlights.length} consolidated visual highlights for "${searchTerm}" using "${bestMatchType}" strategy`);
            
            return finalHighlights;
            
        } catch (err) {
            console.error(`[AdvancedPDFViewer] Error in robust search for "${searchTerm}":`, err);
            return [];
        }
    }, [pdfDocument, pageNum, baseScale]);

    // Render page when document loads or page changes
    useEffect(() => {
        if (pdfDocument && !isLoading && pageNum > 0 && pageNum <= numPages && !isMinimized) {
            console.log(`[AdvancedPDFViewer] Triggering render for page ${pageNum}`);
            renderPage(pageNum);
        }
    }, [pdfDocument, pageNum, isLoading, numPages, isMinimized, renderPage]);

    // Enhanced search with deduplication support - searches for both current term and original merged terms
    const performEnhancedSearch = useCallback(async (searchTerm: string): Promise<SearchMatch[]> => {
        if (!pdfDocument || !searchTerm) {
            console.log(`[AdvancedPDFViewer] Enhanced search skipped - missing pdfDocument (${!!pdfDocument}) or searchTerm (${!!searchTerm})`);
            return [];
        }

        console.log(`[AdvancedPDFViewer] ===== ENHANCED SEARCH DEBUG START =====`);
        console.log(`[AdvancedPDFViewer] Starting enhanced search for "${searchTerm}" on page ${pageNum}`);
        console.log(`[AdvancedPDFViewer] Project ID: ${projectId}`);
        
        // Collect all terms to search for (current term + any original terms that were merged into it)
        const termsToSearch = new Set<string>();
        termsToSearch.add(searchTerm.trim());
        console.log(`[AdvancedPDFViewer] Initial term added: "${searchTerm.trim()}"`);
        
        // Try to get original terms from the CSV data via an API call
        let apiCallSuccess = false;
        let apiError: any = null;
        
        try {
            const encodedTerm = encodeURIComponent(searchTerm);
            const apiUrl = `/api/projects/${projectId}/original-terms/${encodedTerm}`;
            console.log(`[AdvancedPDFViewer] Making API call to: ${apiUrl}`);
            
            // Check if we have auth token - use the correct localStorage key
            const authToken = localStorage.getItem('orca_auth_token');
            console.log(`[AdvancedPDFViewer] Auth token available: ${!!authToken}`);
            if (authToken) {
                console.log(`[AdvancedPDFViewer] Auth token length: ${authToken.length}`);
            }
            
            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            };
            
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            console.log(`[AdvancedPDFViewer] Request headers:`, headers);
            
            // Always hit absolute API URL to avoid dev server HTML fallback
            const { getApiUrl } = await import('../client/lib/config');
            const response = await fetch(getApiUrl(apiUrl), { headers });
            console.log(`[AdvancedPDFViewer] API response status: ${response.status} ${response.statusText}`);
            console.log(`[AdvancedPDFViewer] API response ok: ${response.ok}`);
            
            if (response.ok) {
                // Validate content-type to avoid JSON parse errors when HTML is returned
                const contentType = response.headers.get('content-type') || '';
                if (!contentType.includes('application/json')) {
                    const textPreview = (await response.text()).slice(0, 200);
                    throw new Error(`Expected JSON but received: ${contentType}. Preview: ${textPreview}`);
                }
                const data = await response.json();
                console.log(`[AdvancedPDFViewer] API response data:`, data);
                
                if (data.originalTerms && Array.isArray(data.originalTerms)) {
                    console.log(`[AdvancedPDFViewer] Found ${data.originalTerms.length} original terms:`, data.originalTerms);
                    data.originalTerms.forEach((term: string) => {
                        if (term && term.trim() !== searchTerm.trim()) {
                            termsToSearch.add(term.trim());
                            console.log(`[AdvancedPDFViewer] Added original term for search: "${term}"`);
                        } else {
                            console.log(`[AdvancedPDFViewer] Skipped original term (empty or same as search term): "${term}"`);
                        }
                    });
                    apiCallSuccess = true;
                } else {
                    console.log(`[AdvancedPDFViewer] API response missing or invalid originalTerms array`);
                }
            } else {
                const errorText = await response.text();
                console.error(`[AdvancedPDFViewer] API call failed with status ${response.status}:`);
                console.error(`[AdvancedPDFViewer] Error response body:`, errorText);
                apiError = `${response.status} ${response.statusText}: ${errorText}`;
            }
        } catch (error) {
            console.error(`[AdvancedPDFViewer] API call threw exception:`, error);
            apiError = error;
        }
        
        console.log(`[AdvancedPDFViewer] API call success: ${apiCallSuccess}`);
        if (apiError) {
            console.log(`[AdvancedPDFViewer] API error details:`, apiError);
        }
        
        // Also check termUpdates for any manual edits (existing functionality)
        let termUpdatesChecked = 0;
        if (termUpdates && Object.keys(termUpdates).length > 0) {
            console.log(`[AdvancedPDFViewer] Checking ${Object.keys(termUpdates).length} term updates for manual edits`);
            for (const [originalTerm, editedTerm] of Object.entries(termUpdates)) {
                termUpdatesChecked++;
                if (editedTerm.toLowerCase() === searchTerm.toLowerCase()) {
                    termsToSearch.add(originalTerm.trim());
                    console.log(`[AdvancedPDFViewer] Added manually edited original term: "${originalTerm}" (was edited to "${editedTerm}")`);
                }
            }
        } else {
            console.log(`[AdvancedPDFViewer] No term updates to check (termUpdates: ${!!termUpdates}, keys: ${termUpdates ? Object.keys(termUpdates).length : 0})`);
        }
        
        console.log(`[AdvancedPDFViewer] Final terms to search (${termsToSearch.size} total):`, Array.from(termsToSearch));
        
        // Search for all terms and combine results
        const allMatches: SearchMatch[] = [];
        let searchTermsUsed: string[] = [];
        let searchCount = 0;
        
        for (const term of termsToSearch) {
            searchCount++;
            console.log(`[AdvancedPDFViewer] Searching ${searchCount}/${termsToSearch.size} for term: "${term}"`);
            const matches = await searchInPage(term);
            console.log(`[AdvancedPDFViewer] Search for "${term}" found ${matches.length} matches`);
            
            if (matches.length > 0) {
                allMatches.push(...matches);
                searchTermsUsed.push(term);
                console.log(`[AdvancedPDFViewer] Added ${matches.length} matches for "${term}" (total matches now: ${allMatches.length})`);
            }
        }
        
        console.log(`[AdvancedPDFViewer] Search complete - found matches for ${searchTermsUsed.length} terms: [${searchTermsUsed.join(', ')}]`);
        console.log(`[AdvancedPDFViewer] Total raw matches before deduplication: ${allMatches.length}`);
        
        // Remove duplicate highlights that are too close to each other
        const consolidatedMatches: SearchMatch[] = [];
        const DUPLICATE_THRESHOLD = 10; // pixels
        let duplicatesRemoved = 0;
        
        for (const match of allMatches) {
            const isDuplicate = consolidatedMatches.some(existing => {
                const distance = Math.sqrt(
                    Math.pow(match.x - existing.x, 2) + Math.pow(match.y - existing.y, 2)
                );
                return distance < DUPLICATE_THRESHOLD;
            });
            
            if (!isDuplicate) {
                consolidatedMatches.push(match);
            } else {
                duplicatesRemoved++;
            }
        }
        
        console.log(`[AdvancedPDFViewer] Deduplication: removed ${duplicatesRemoved} duplicate matches`);
        console.log(`[AdvancedPDFViewer] Final consolidated matches: ${consolidatedMatches.length}`);
        
        if (consolidatedMatches.length > 0) {
            const uniqueTermsFound = searchTermsUsed.length;
            const totalMatches = consolidatedMatches.length;
            
            if (uniqueTermsFound > 1) {
                console.log(`[AdvancedPDFViewer] ✅ Enhanced search SUCCESS: found ${totalMatches} matches across ${uniqueTermsFound} related terms`);
            } else {
                console.log(`[AdvancedPDFViewer] ✅ Standard search SUCCESS: found ${totalMatches} matches for primary term only`);
            }
        } else {
            console.log(`[AdvancedPDFViewer] ❌ No matches found for any searched terms`);
        }
        
        console.log(`[AdvancedPDFViewer] ===== ENHANCED SEARCH DEBUG END =====`);
        
        return consolidatedMatches;
    }, [pdfDocument, pageNum, projectId, termUpdates, searchInPage]);

    // Optimized search with debouncing and caching
    useEffect(() => {
        // Clear previous search timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = null;
        }

        const performSearch = async () => {
            if (!highlightedTerm || !pdfDocument || isLoading || pageNum < 1 || pageNum > numPages) {
                setSearchMatches([]);
                setDebugInfo('');
                lastSearchRef.current = null;
                return;
            }

            // Check if this is the same search as last time (caching)
            const currentSearch = { term: highlightedTerm, page: pageNum };
            if (lastSearchRef.current && 
                lastSearchRef.current.term === currentSearch.term && 
                lastSearchRef.current.page === currentSearch.page) {
                console.log(`[AdvancedPDFViewer] Using cached search results for "${highlightedTerm}" on page ${pageNum}`);
                return;
            }

            try {
                setIsSearching(true);
                console.log(`[AdvancedPDFViewer] Starting enhanced search for "${highlightedTerm}" on page ${pageNum}`);
                setDebugInfo(`Searching for "${highlightedTerm}"...`);
                
                // Use enhanced search that includes original merged terms
                const matches = await performEnhancedSearch(highlightedTerm);
                
                setSearchMatches(matches);
                lastSearchRef.current = currentSearch;
                
                if (matches.length > 0) {
                    setDebugInfo(`✓ Found ${matches.length} match(es)`);
                    console.log(`[AdvancedPDFViewer] Enhanced search found ${matches.length} matches for "${highlightedTerm}"`);
                } else {
                    setDebugInfo(`No matches found for "${highlightedTerm}"`);
                    console.log(`[AdvancedPDFViewer] No matches found for "${highlightedTerm}" on page ${pageNum}`);
                }
                
            } catch (err) {
                console.error(`[AdvancedPDFViewer] Error in enhanced search for "${highlightedTerm}":`, err);
                setDebugInfo(`Search error: ${err}`);
            } finally {
                setIsSearching(false);
            }
        };

        // Debounce search with longer delay to prevent excessive searching
        searchTimeoutRef.current = setTimeout(performSearch, 300);
        
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
        };
    }, [highlightedTerm, pageNum, pdfDocument, isLoading, numPages, performEnhancedSearch]);

    // Function to scroll to a specific match by index
    const scrollToMatch = useCallback((matchIndex: number) => {
        if (searchMatches.length === 0 || !containerRef.current || !overlayRef.current) {
            return;
        }

        const targetMatch = searchMatches[matchIndex];
        if (!targetMatch) return;

        try {
            console.log(`[AdvancedPDFViewer] Scrolling to match ${matchIndex + 1}/${searchMatches.length} at position (${targetMatch.x}, ${targetMatch.y})`);

            // Calculate the position of the highlight in the scaled coordinate system
            const scaledY = targetMatch.y;
            const scaledX = targetMatch.x;
            const scaledWidth = targetMatch.width;
            const scaledHeight = targetMatch.height;

            // Get container dimensions
            const containerRect = containerRef.current!.getBoundingClientRect();
            const containerScrollTop = containerRef.current!.scrollTop;
            const containerScrollLeft = containerRef.current!.scrollLeft;

            // Calculate target scroll positions to center the highlight both vertically and horizontally
            const targetScrollTop = Math.max(0, scaledY - (containerRect.height / 2) + (scaledHeight / 2));
            const targetScrollLeft = Math.max(0, scaledX - (containerRect.width / 2) + (scaledWidth / 2));

            // Check if the highlight is visible both vertically and horizontally
            const highlightTop = scaledY;
            const highlightBottom = scaledY + scaledHeight;
            const highlightLeft = scaledX;
            const highlightRight = scaledX + scaledWidth;
            
            const visibleTop = containerScrollTop;
            const visibleBottom = containerScrollTop + containerRect.height;
            const visibleLeft = containerScrollLeft;
            const visibleRight = containerScrollLeft + containerRect.width;

            const isVerticallyVisible = highlightTop >= visibleTop && highlightBottom <= visibleBottom;
            const isHorizontallyVisible = highlightLeft >= visibleLeft && highlightRight <= visibleRight;

            // Always scroll to center the current match, even if it's partially visible
            console.log(`[AdvancedPDFViewer] Centering match ${matchIndex + 1} at (${targetScrollLeft}, ${targetScrollTop})`);
            
            // Smooth scroll to the highlight (both horizontal and vertical)
            containerRef.current!.scrollTo({
                top: targetScrollTop,
                left: targetScrollLeft,
                behavior: 'smooth'
            });

        } catch (err) {
            console.error(`[AdvancedPDFViewer] Error scrolling to match ${matchIndex}:`, err);
        }
    }, [searchMatches]);

    // Auto-scroll to first highlight when search matches are found
    useEffect(() => {
        if (searchMatches.length > 0 && containerRef.current && overlayRef.current && !isSearching) {
            // Reset to first match when new search results come in
            setCurrentMatchIndex(0);
            
            // Small delay to ensure highlights are rendered
            const scrollTimeout = setTimeout(() => {
                scrollToMatch(0);
            }, 100); // Small delay to ensure DOM is updated

            return () => clearTimeout(scrollTimeout);
        }
    }, [searchMatches, isSearching, scrollToMatch]);

    const handlePrevPage = () => {
        if (pageNum > 1) {
            console.log(`[AdvancedPDFViewer] Manual navigation: previous page ${pageNum} -> ${pageNum - 1}`);
            setHasManuallyNavigated(true);
            lastManualNavigationTimeRef.current = Date.now(); // Track manual navigation time
            setPageNum(pageNum - 1);
            
            // Clear any existing timeout (no longer using timeout-based reset)
            if (manualNavigationTimeoutRef.current) {
                clearTimeout(manualNavigationTimeoutRef.current);
                manualNavigationTimeoutRef.current = null;
            }
        }
    };

    const handleNextPage = () => {
        if (pageNum < numPages) {
            console.log(`[AdvancedPDFViewer] Manual navigation: next page ${pageNum} -> ${pageNum + 1}`);
            setHasManuallyNavigated(true);
            lastManualNavigationTimeRef.current = Date.now(); // Track manual navigation time
            setPageNum(pageNum + 1);
            
            // Clear any existing timeout (no longer using timeout-based reset)
            if (manualNavigationTimeoutRef.current) {
                clearTimeout(manualNavigationTimeoutRef.current);
                manualNavigationTimeoutRef.current = null;
            }
        }
    };

    const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPageInputValue(e.target.value);
    };

    const handlePageInputSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const targetPage = parseInt(pageInputValue);
        if (!isNaN(targetPage) && targetPage >= 1 && targetPage <= numPages) {
            console.log(`[AdvancedPDFViewer] Manual page input navigation: ${pageNum} -> ${targetPage}`);
            setHasManuallyNavigated(true);
            lastManualNavigationTimeRef.current = Date.now(); // Track manual navigation time
            setPageNum(targetPage);
            
            // Clear any existing timeout (no longer using timeout-based reset)
            if (manualNavigationTimeoutRef.current) {
                clearTimeout(manualNavigationTimeoutRef.current);
                manualNavigationTimeoutRef.current = null;
            }
        } else {
            setPageInputValue(pageNum.toString());
        }
    };

    const handleClearHighlight = () => {
        console.log(`[AdvancedPDFViewer] Clearing highlights`);
        setSearchMatches([]);
        setDebugInfo('');
        setCurrentMatchIndex(0);
        
        // Reset manual navigation flag to allow new term-based navigation
        console.log(`[AdvancedPDFViewer] Resetting manual navigation flag to allow fresh term navigation`);
        setHasManuallyNavigated(false);
        lastManualNavigationTimeRef.current = null;
        
        if (onClearHighlight) {
            onClearHighlight();
        }
    };

    // Navigation functions for cycling through matches
    const goToNextMatch = useCallback(() => {
        if (searchMatches.length === 0) return;
        
        const nextIndex = (currentMatchIndex + 1) % searchMatches.length;
        setCurrentMatchIndex(nextIndex);
        scrollToMatch(nextIndex);
    }, [currentMatchIndex, searchMatches.length, scrollToMatch]);

    const goToPrevMatch = useCallback(() => {
        if (searchMatches.length === 0) return;
        
        const prevIndex = currentMatchIndex === 0 ? searchMatches.length - 1 : currentMatchIndex - 1;
        setCurrentMatchIndex(prevIndex);
        scrollToMatch(prevIndex);
    }, [currentMatchIndex, searchMatches.length, scrollToMatch]);

    // Keyboard event handler for arrow key navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Only handle arrow keys when there are search matches and the PDF viewer is focused
            if (searchMatches.length === 0) return;
            
            // Check if the focus is within the PDF viewer container
            const isWithinPDFViewer = containerRef.current?.contains(document.activeElement) || 
                                     document.activeElement === containerRef.current;
            
            if (!isWithinPDFViewer) return;

            switch (event.key) {
                case 'ArrowDown':
                case 'ArrowRight':
                    event.preventDefault();
                    goToNextMatch();
                    break;
                case 'ArrowUp':
                case 'ArrowLeft':
                    event.preventDefault();
                    goToPrevMatch();
                    break;
            }
        };

        // Add event listener to the container
        const container = containerRef.current;
        if (container) {
            container.addEventListener('keydown', handleKeyDown);
            // Make container focusable
            container.setAttribute('tabindex', '0');
            
            return () => {
                container.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [searchMatches.length, goToNextMatch, goToPrevMatch]);

    const handleRetryRender = () => {
        console.log(`[AdvancedPDFViewer] Retry render for page ${pageNum}`);
        setRenderError(null);
        renderPage(pageNum);
    };

    const handleToggleMinimize = () => {
        if (onToggleMinimize) {
            onToggleMinimize();
        }
    };

    // Handle visibility changes more carefully to prevent unwanted resets
    useEffect(() => {
        if (!isMinimized && pdfDocument && pageNum > 0 && pageNum <= numPages) {
            // Component became visible and we have a valid state
            console.log(`[AdvancedPDFViewer] Component became visible, ensuring page ${pageNum} is rendered`);
            
            // Only render if we don't currently have a canvas (avoid unnecessary re-renders)
            if (!currentCanvasRef.current && !isRenderingRef.current) {
                console.log(`[AdvancedPDFViewer] No canvas found after visibility change, triggering render for page ${pageNum}`);
                
                // Reset rendering state to ensure clean render
                isRenderingRef.current = false;
                
                // Clear any render errors
                setRenderError(null);
                
                // Small delay to ensure DOM is ready
                setTimeout(() => {
                    if (!isMinimized && pdfDocument && pageNum > 0 && pageNum <= numPages && !currentCanvasRef.current) {
                        renderPage(pageNum);
                    }
                }, 100);
            }
        } else if (isMinimized) {
            // Component was minimized - clean up without resetting page state
            console.log('[AdvancedPDFViewer] Component minimized, cleaning up render state');
            
            // Cancel any active render tasks
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
                renderTaskRef.current = null;
            }
            
            // Reset rendering state
            isRenderingRef.current = false;
            
            // Clear search state
            setSearchMatches([]);
            setIsSearching(false);
            setRenderError(null);
            
            // Clear search cache
            lastSearchRef.current = null;
        }
    }, [isMinimized, pdfDocument, pageNum, numPages, renderPage]);

    // Recovery mechanism for stuck loading states - SIMPLIFIED TO PREVENT CONFLICTS
    useEffect(() => {
        if (!isMinimized && pdfDocument && !isRenderingRef.current && !currentCanvasRef.current && pageNum > 0 && pageNum <= numPages) {
            // If we're visible, have a document, not currently rendering, but no canvas, we might be stuck
            const timer = setTimeout(() => {
                // Double-check conditions before recovery attempt
                if (!isMinimized && pdfDocument && !currentCanvasRef.current && pageNum > 0 && pageNum <= numPages && !isRenderingRef.current) {
                    console.log('[AdvancedPDFViewer] Detected stuck state, attempting recovery...');
                    setRenderError(null);
                    isRenderingRef.current = false;
                    renderPage(pageNum);
                }
            }, 2000); // Increased delay to prevent conflicts with normal rendering

            return () => clearTimeout(timer);
        }
    }, [isMinimized, pdfDocument, pageNum, numPages, renderPage]);

    // Cleanup when component is minimized
    useEffect(() => {
        if (isMinimized) {
            console.log('[AdvancedPDFViewer] Component minimized, cleaning up active operations');
            
            // Cancel any active render tasks
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
                renderTaskRef.current = null;
            }
            
            // Cancel any active search operations
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
            
            // Reset rendering state
            isRenderingRef.current = false;
            
            // Clear all search state completely
            setIsSearching(false);
            setSearchMatches([]);
            setCurrentMatchIndex(0);
            setDebugInfo('');
            setRenderError(null);
            
            // Clear search cache
            lastSearchRef.current = null;
        }
    }, [isMinimized]);

    // Loading timeout detection to prevent infinite loading
    useEffect(() => {
        if (isLoading && !isMinimized) {
            const loadingTimeout = setTimeout(() => {
                if (isLoading && !isMinimized) {
                    console.log('[AdvancedPDFViewer] Loading timeout detected, attempting recovery');
                    setIsLoading(false);
                    if (!pdfDocument && !error) {
                        setError('Loading timeout - please try the retry button');
                    }
                }
            }, 10000); // 10 second timeout

            return () => clearTimeout(loadingTimeout);
        }
    }, [isLoading, isMinimized, pdfDocument, error]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (DEBUG_MEMORY) console.log('[PDF-CLEANUP] AdvancedPDFViewer unmounting, cleaning up resources');
            
            // Cancel any ongoing render task
            if (renderTaskRef.current) {
                if (DEBUG_MEMORY) console.log('[PDF-CLEANUP] Cancelling render task');
                renderTaskRef.current.cancel();
                renderTaskRef.current = null;
            }
            
            // Clear search timeout
            if (searchTimeoutRef.current) {
                if (DEBUG_MEMORY) console.log('[PDF-CLEANUP] Clearing search timeout');
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
            
            // Clear PDF loading debouncing timeout
            if (loadTimeoutRef.current) {
                if (DEBUG_MEMORY) console.log('[PDF-CLEANUP] Clearing PDF loading timeout');
                clearTimeout(loadTimeoutRef.current);
                loadTimeoutRef.current = null;
            }
            
            // Reset PDF loading state
            isLoadingPDFRef.current = false;
            currentLoadKeyRef.current = '';
            pendingLoadRef.current = null;
            loadedPDFKeyRef.current = ''; // Clear loaded PDF tracking
            
            // Clean up PDF document
            if (pdfDocument) {
                if (DEBUG_MEMORY) console.log('[PDF-CLEANUP] Destroying PDF document');
                pdfDocument.destroy();
            }
            
            // Clear canvas references
            if (currentCanvasRef.current) {
                if (DEBUG_MEMORY) console.log('[PDF-CLEANUP] Clearing canvas reference');
                currentCanvasRef.current = null;
            }
        };
    }, [pdfDocument]);

    // The external navigation effect above handles currentPage prop changes properly

    if (isMinimized) {
        return (
            <div className="relative flex flex-col h-full">
                {onToggleMinimize && (
                    <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-50">
                        <Button
                            onClick={handleToggleMinimize}
                            className="bg-card hover:bg-muted text-foreground border border-border shadow-md hover:shadow-lg transition-all duration-200 w-6 h-6 p-0 rounded"
                            variant="outline"
                            size="sm"
                            title="Expand Document"
                        >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 12L12 4M12 4H6M12 4V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-background relative">
            {/* Debug information */}
            {DEBUG_MEMORY && (
                <div className="absolute top-0 right-0 bg-red-100 border border-red-300 text-xs p-2 m-2 rounded z-50 max-w-sm overflow-auto max-h-96">
                    <div><strong>PDF Debug:</strong></div>
                    <div>Loading: {isLoading ? 'YES' : 'NO'}</div>
                    <div>Error: {error ? 'YES' : 'NO'}</div>
                    <div>RenderError: {renderError ? 'YES' : 'NO'}</div>
                    <div>PDF Doc: {pdfDocument ? 'YES' : 'NO'}</div>
                    <div>Minimized: {isMinimized ? 'YES' : 'NO'}</div>
                    <div>ProjectId: {projectId}</div>
                    <div>Filename: {filename}</div>
                    <div>Pages: {numPages}</div>
                    <div>Current: {pageNum}</div>
                    
                    <div className="border-t border-red-400 mt-2 pt-2">
                        <div><strong>Canvas Debug:</strong></div>
                        <div>Canvas Ref: {currentCanvasRef.current ? 'EXISTS' : 'NULL'}</div>
                        <div>Container Ref: {canvasContainerRef.current ? 'EXISTS' : 'NULL'}</div>
                        <div>Canvas Width: {canvasWidth}</div>
                        <div>Canvas Height: {canvasHeight}</div>
                        <div>Is Rendering: {isRenderingRef.current ? 'YES' : 'NO'}</div>
                        {currentCanvasRef.current && (
                            <>
                                <div>Canvas DOM Width: {currentCanvasRef.current.width}</div>
                                <div>Canvas DOM Height: {currentCanvasRef.current.height}</div>
                                <div>Canvas Style Width: {currentCanvasRef.current.style.width}</div>
                                <div>Canvas Style Height: {currentCanvasRef.current.style.height}</div>
                                <div>Canvas Parent: {currentCanvasRef.current.parentElement ? 'HAS_PARENT' : 'NO_PARENT'}</div>
                            </>
                        )}
                        {canvasContainerRef.current && (
                            <>
                                <div>Container Children: {canvasContainerRef.current.children.length}</div>
                                <div>Container Width: {canvasContainerRef.current.offsetWidth}</div>
                                <div>Container Height: {canvasContainerRef.current.offsetHeight}</div>
                                <div>Container Visible: {canvasContainerRef.current.offsetParent ? 'YES' : 'NO'}</div>
                            </>
                        )}
                    </div>
                    
                    <div className="border-t border-red-400 mt-2 pt-2">
                        <div><strong>Render Debug:</strong></div>
                        <div>Last Render Attempt: {lastRenderAttemptRef.current || 'NONE'}</div>
                        <div>Render Success: {lastRenderSuccessRef.current || 'NONE'}</div>
                        <div>Component Mount Time: {new Date(componentMountTimeRef.current).toLocaleTimeString()}</div>
                        <div>Is Initializing: {isInitializingRef.current ? 'YES' : 'NO'}</div>
                    </div>
                    
                    <div className="border-t border-red-400 mt-2 pt-2">
                        <div><strong>CSS Debug:</strong></div>
                        {containerRef.current && (
                            <>
                                <div>Main Container Classes: {containerRef.current.className}</div>
                                <div>Main Container Computed Display: {window.getComputedStyle(containerRef.current).display}</div>
                                <div>Main Container Computed Visibility: {window.getComputedStyle(containerRef.current).visibility}</div>
                                <div>Main Container Computed Opacity: {window.getComputedStyle(containerRef.current).opacity}</div>
                                <div>Main Container Computed Z-Index: {window.getComputedStyle(containerRef.current).zIndex}</div>
                            </>
                        )}
                        {canvasContainerRef.current && (
                            <>
                                <div>Canvas Container Classes: {canvasContainerRef.current.className}</div>
                                <div>Canvas Container Computed Display: {window.getComputedStyle(canvasContainerRef.current).display}</div>
                                <div>Canvas Container Computed Visibility: {window.getComputedStyle(canvasContainerRef.current).visibility}</div>
                                <div>Canvas Container Computed Opacity: {window.getComputedStyle(canvasContainerRef.current).opacity}</div>
                            </>
                        )}
                        {currentCanvasRef.current && (
                            <>
                                <div>Canvas Classes: {currentCanvasRef.current.className}</div>
                                <div>Canvas Computed Display: {window.getComputedStyle(currentCanvasRef.current).display}</div>
                                <div>Canvas Computed Visibility: {window.getComputedStyle(currentCanvasRef.current).visibility}</div>
                                <div>Canvas Computed Opacity: {window.getComputedStyle(currentCanvasRef.current).opacity}</div>
                                <div>Canvas Position: {window.getComputedStyle(currentCanvasRef.current).position}</div>
                                <div>Canvas Transform: {window.getComputedStyle(currentCanvasRef.current).transform}</div>
                            </>
                        )}
                        <div className="mt-1">
                            <div><strong>Parent Hierarchy:</strong></div>
                            {containerRef.current && (
                                <>
                                    <div>Parent Classes: {containerRef.current.parentElement?.className || 'NO_PARENT'}</div>
                                    <div>Grandparent Classes: {containerRef.current.parentElement?.parentElement?.className || 'NO_GRANDPARENT'}</div>
                                    {containerRef.current.parentElement && (
                                        <>
                                            <div>Parent Display: {window.getComputedStyle(containerRef.current.parentElement).display}</div>
                                            <div>Parent Overflow: {window.getComputedStyle(containerRef.current.parentElement).overflow}</div>
                                            <div>Parent Height: {window.getComputedStyle(containerRef.current.parentElement).height}</div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    
                    {error && <div className="text-red-600 mt-1 border-t border-red-400 pt-1">Error: {error}</div>}
                    {renderError && <div className="text-red-600 mt-1 border-t border-red-400 pt-1">Render: {renderError}</div>}
                </div>
            )}
            
            {/* Toolbar is outside the scroll container, positioned absolutely to the main container */}
            {!isLoading && !error && !renderError && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
                    <div className="flex items-center space-x-2 bg-card/90 backdrop-blur-sm rounded-full shadow-md border border-border/75 px-3 py-1 pointer-events-auto">
                        {/* Page Navigation */}
                        <div className="flex items-center space-x-1">
                            <button onClick={handlePrevPage} disabled={pageNum <= 1} className="p-1 rounded-full text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                            </button>
                            <form onSubmit={handlePageInputSubmit} className="flex items-center">
                                <input
                                    type="text"
                                    value={pageInputValue}
                                    onChange={handlePageInputChange}
                                    className="w-8 p-0 text-xs text-center font-medium bg-transparent text-foreground rounded-md border-none focus:ring-0"
                                />
                                <span className="text-xs text-muted-foreground">of {numPages || '...'}</span>
                            </form>
                            <button onClick={handleNextPage} disabled={pageNum >= numPages} className="p-1 rounded-full text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                            </button>
                        </div>
                        
                        {/* Minimize Button */}
                        {onToggleMinimize && (
                            <div className="flex items-center space-x-1 border-l border-border pl-2">
                                <button 
                                    onClick={handleToggleMinimize}
                                    className="p-1 rounded-full text-muted-foreground hover:bg-muted transition-colors"
                                    title="Minimize Document"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4"></path>
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Content Area */}
            <div className="flex-1 relative min-h-0" style={{ minHeight: '593px' }}>
                <div 
                    ref={containerRef} 
                    className="absolute inset-0 overflow-y-auto bg-muted/10"
                >
                    {isLoading ? (
                        <div className="flex justify-center items-center min-h-full">
                            <div className="text-center text-muted-foreground">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                                <p>Loading document...</p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="flex justify-center items-center min-h-full">
                            <div className="text-center text-destructive p-4">
                                <p className="font-semibold">Error Loading PDF</p>
                                <p className="text-sm mt-1">{error}</p>
                                <button onClick={() => debouncedLoadPDF(projectId, filename)} className="mt-4 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90">Retry</button>
                            </div>
                        </div>
                    ) : renderError ? (
                        <div className="flex justify-center items-center min-h-full">
                            <div className="text-center text-destructive p-4">
                                <p className="font-semibold">Error Rendering Page</p>
                                <p className="text-sm mt-1">{renderError}</p>
                                <button onClick={handleRetryRender} className="mt-4 px-4 py-2 text-sm font-medium text-primary-foreground bg-destructive rounded-md hover:bg-destructive/90">Retry Render</button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-start items-start bg-muted/5">
                            <div className="py-20"> {/* Padding to clear the absolute toolbar and provide space */}
                                <div 
                                    className="relative"
                                    style={{
                                        width: canvasWidth > 0 ? `${canvasWidth}px` : 'auto',
                                        maxWidth: canvasWidth > 0 ? `${canvasWidth}px` : 'none',
                                        height: canvasHeight > 0 ? `${canvasHeight}px` : 'auto',
                                    }}
                                >
                                    <div
                                        ref={canvasContainerRef}
                                        className="bg-muted/10 shadow-lg w-full h-full border border-border/20 rounded-lg"
                                    >
                                        {/* Canvas is appended here. */}
                                    </div>
                                    
                                    {searchMatches.length > 0 && canvasWidth > 0 && (
                                        <div
                                            ref={overlayRef}
                                            className="absolute pointer-events-none top-0 left-0 w-full h-full"
                                        >
                                            {searchMatches.map((match, index) => (
                                                <div
                                                    key={index}
                                                    className={`absolute ${currentMatchIndex === index ? 'bg-yellow-400' : 'bg-yellow-300'} opacity-50 border-2 ${currentMatchIndex === index ? 'border-orange-500' : 'border-yellow-400'}`}
                                                    style={{
                                                        left: `${(match.x / canvasWidth) * 100}%`,
                                                        top: `${(match.y / canvasHeight) * 100}%`,
                                                        width: `${(match.width / canvasWidth) * 100}%`,
                                                        height: `${(match.height / canvasHeight) * 100}%`,
                                                    }}
                                                    title={`Match ${index + 1}`}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};